import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import ReactApexChart from "react-apexcharts";
import { Table, ButtonGroup, Button } from "react-bootstrap";
import ExportButton from "./ExportButton";
import { exportToExcel } from "./exportExcel";

const MONTH_LABELS = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];

// Meses cobertos pelo período, com a fração de dias de cada um para proporcionalizar a meta mensal
function periodMonths(startDate, endDate) {
    const start = dayjs(startDate).startOf("day");
    const end = dayjs(endDate).startOf("day");
    const months = [];

    let cursor = start.startOf("month");
    while (!cursor.isAfter(end)) {
        const from = start.isAfter(cursor) ? start : cursor;
        const monthEnd = cursor.endOf("month").startOf("day");
        const to = end.isBefore(monthEnd) ? end : monthEnd;

        months.push({
            month: cursor.month() + 1,
            year: cursor.year(),
            label: `${MONTH_LABELS[cursor.month()]}/${cursor.format("YY")}`,
            fraction: (to.diff(from, "day") + 1) / cursor.daysInMonth()
        });

        cursor = cursor.add(1, "month");
    }

    return months;
}

const HospitalGoal = props => {
    const {
        hospitalSummary,
        hospitals,
        startDate,
        endDate
    } = props;

    const [viewMode, setViewMode] = useState("table");

    const months = useMemo(() => periodMonths(startDate, endDate), [startDate, endDate]);

    const rows = useMemo(() => {
        const factor = months.reduce((total, m) => total + m.fraction, 0);

        return (hospitals || [])
            .filter(hospital => Number(hospital.monthly_goal) > 0)
            .map(hospital => {
                const items = (hospitalSummary || []).filter(x => String(x.hospital_id) === String(hospital._id));
                const byMonth = months.map(m => items
                    .filter(x => x.month === m.month && x.year === m.year)
                    .reduce((total, x) => total + x.count, 0));
                const done = byMonth.reduce((total, value) => total + value, 0);
                const goal = Number(hospital.monthly_goal) * factor;

                return {
                    id: hospital._id,
                    name: hospital.name,
                    monthlyGoal: Number(hospital.monthly_goal),
                    byMonth,
                    done,
                    goal,
                    percent: goal > 0 ? (done * 100) / goal : 0
                };
            })
            .sort((a, b) => b.percent - a.percent);
    }, [hospitals, hospitalSummary, months]);

    const chartOptions = useMemo(() => ({
        chart: { type: "bar", height: 350, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: "70%", borderRadius: 4 } },
        colors: ["#374B45", "#C86B2A"],
        dataLabels: { enabled: false },
        legend: { show: true, position: "top" },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
            categories: rows.map(r => r.name),
            labels: { style: { colors: "#000000", fontSize: "12px", fontFamily: "poppins", fontWeight: 400 } }
        },
        yaxis: {
            labels: { style: { colors: "#000000", fontSize: "12px", fontFamily: "poppins", fontWeight: 400 } }
        },
        tooltip: { y: { formatter: val => val } },
        title: {
            text: "Realizado x meta no período",
            style: { fontFamily: "poppins", fontWeight: 600 }
        }
    }), [rows]);

    const chartSeries = useMemo(() => [
        { name: "Realizado", data: rows.map(r => r.done) },
        { name: "Meta", data: rows.map(r => Number(r.goal.toFixed(1))) }
    ], [rows]);

    function formatGoal(value) {
        return Number(value.toFixed(1)).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
    }

    function formatPercent(value) {
        return `${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
    }

    function handleExport() {
        const exportRows = rows.map(r => {
            const row = { Hospital: r.name, "Meta mensal": r.monthlyGoal };
            months.forEach((m, i) => { row[m.label] = r.byMonth[i]; });
            row["Realizado"] = r.done;
            row["Meta do período"] = Number(r.goal.toFixed(1));
            row["% atingido"] = Number(r.percent.toFixed(2));
            return row;
        });

        exportToExcel(exportRows, "Meta por hospital", "procedimentos_x_meta_por_hospital");
    }

    return (
        <>
            <div className="row">
                <div className="col-xl-8">
                    <div className="card-header border-0 flex-wrap pb-2">
                        <div className="chart-title mb-2 ">
                            <h2 className="heading">Procedimentos × Meta por Hospital</h2>
                        </div>
                    </div>
                </div>
                <div className="col-xl-4">
                    <div className="d-flex justify-content-end gap-2 align-items-center">
                        <ButtonGroup aria-label="View Mode">
                            <Button
                                variant={viewMode === "table" ? "primary" : "light"}
                                onClick={() => setViewMode("table")}
                            >
                                Tabela
                            </Button>
                            <Button
                                variant={viewMode === "chart" ? "primary" : "light"}
                                onClick={() => setViewMode("chart")}
                            >
                                Indicador
                            </Button>
                        </ButtonGroup>
                        {rows.length > 0 && <ExportButton onClick={handleExport} />}
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-12">
                    <div className="card-body pt-0 custome-tooltip">
                        {rows.length === 0 ? (
                            <p className="mb-0">Nenhum hospital com meta cadastrada. Informe a meta mensal de procedimentos no cadastro de Hospitais.</p>
                        ) : viewMode === "table" ? (
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th><strong>HOSPITAL</strong></th>
                                        <th className="text-center"><strong>META MENSAL</strong></th>
                                        {months.map(m => (
                                            <th key={m.label} className="text-center"><strong>{m.label}</strong></th>
                                        ))}
                                        <th className="text-center"><strong>REALIZADO</strong></th>
                                        <th className="text-center"><strong>META DO PERÍODO</strong></th>
                                        <th className="text-center"><strong>% ATINGIDO</strong></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => (
                                        <tr key={r.id}>
                                            <td><span className="w-space-no">{r.name}</span></td>
                                            <td><div className="text-center">{r.monthlyGoal}</div></td>
                                            {r.byMonth.map((value, i) => (
                                                <td key={months[i].label}><div className="text-center">{value}</div></td>
                                            ))}
                                            <td><div className="text-center"><strong>{r.done}</strong></div></td>
                                            <td><div className="text-center">{formatGoal(r.goal)}</div></td>
                                            <td><div className="text-end">{formatPercent(r.percent)}</div></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={Math.max(250, rows.length * 60)} />
                        )}
                        <p className="fs-12 mb-0 mt-2">Meta do período: meta mensal proporcional aos dias do período selecionado. Hospitais sem meta cadastrada não aparecem.</p>
                    </div>
                </div>
            </div>
        </>
    );
};

HospitalGoal.propTypes = {
    hospitalSummary: PropTypes.array,
    hospitals: PropTypes.array,
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date)
};

HospitalGoal.defaultProps = {
    hospitalSummary: [],
    hospitals: []
};

export default HospitalGoal;
